import { ref, computed, watch } from 'vue';

/**
 * TaskTable (v3 homepage) — core logic, shared by both portals.
 *
 * ONE widget, two persona modes selected by the `manager` flag in the widget
 * settings (prototype V3TaskTable `mode`):
 *
 * - **review** (default): backed by the cross-user review-queue endpoint
 *   (`POST Report/reviewQueue`). Rows carry a canonical `status_key`
 *   (breach / overdue / pending-approval / re-review / approved /
 *   pending-review), owner (evaluator), recommended level and due/overdue.
 *   Columns: Activity · Document · Evaluated by · Status · Reference.
 *
 * - **manager**: backed by the user's own task list (injected via `getTasks`,
 *   same source as MyTasks — no HTTP here). Rows are normalised to the
 *   prototype's manager statuses (ready / in-progress / returned / submitted /
 *   approved) from the client-configured lifecycle status name, same
 *   heuristics as taskList.js. Columns: Due · Document · Status.
 *
 * `self` pattern; caller injects an axios-like `http` (review) and/or task
 * getters (manager). Navigation stays in the presentation layer.
 *
 * @param {Object} options
 * @param {{post:Function}} [options.http]       required for review mode
 * @param {() => any[]}   [options.getTasks]     manager mode task rows
 * @param {() => boolean} [options.getTasksLoading]
 * @param {() => number}  [options.getLimit]     display rows before "Show all"
 * @param {() => ('all'|'mine')} [options.getScope]
 * @param {() => ('review'|'manager')} [options.getMode]
 * @param {() => string[]} [options.getDocTypes] doc-type include filter (tmpl_name; blank = all)
 * @param {() => void} [options.onReady] called once after the first load
 */

// review status_key → { label, colour token, icon, action }. Lifecycle status
// names are client-configured; the endpoint maps them to these canonical keys.
const STATUS_META = {
  breach: { label: 'Anomaly', colour: 'var(--v3-attn, #C8447C)', icon: 'flag', action: 'Review' },
  overdue: { label: 'Overdue', colour: 'var(--v3-warn, #B27B12)', icon: 'warning', action: 'Chase' },
  'pending-approval': { label: 'Needs approval', colour: 'var(--v3-accent)', icon: 'how_to_reg', action: 'Approve' },
  're-review': { label: 'Revised', colour: 'var(--v3-revise, #6D5AE6)', icon: 'undo', action: 'Review' },
  'pending-review': { label: 'Awaiting review', colour: 'var(--v3-accent)', icon: 'rate_review', action: 'Review' },
  approved: { label: 'Approved', colour: 'var(--success-fg, #1F7A53)', icon: 'check_circle', action: 'View' },
};
// manager status_key → same shape (prototype V3_SPILL + V3_TT_ACTION).
const MANAGER_STATUS_META = {
  ready: { label: 'Open', colour: 'var(--v3-todo, #5A6678)', icon: 'radio_button_unchecked', action: 'Open' },
  'in-progress': { label: 'In progress', colour: 'var(--v3-accent)', icon: 'schedule', action: 'Continue' },
  returned: { label: 'Sent back', colour: 'var(--v3-revise, #6D5AE6)', icon: 'undo', action: 'Revise' },
  submitted: { label: 'Submitted', colour: 'var(--v3-accent)', icon: 'schedule_send', action: 'Open' },
  approved: { label: 'Approved', colour: 'var(--success-fg, #1F7A53)', icon: 'check_circle', action: 'View' },
};
const FALLBACK_META = { label: '', colour: '#5A6678', icon: 'radio_button_unchecked', action: 'Review' };
const SERVER_LIMIT = 200;

// Fixed status tabs per persona mode — mirrors the prototype V3_TT_TABS, so the
// queue reads as a stable segmented filter (with live counts) rather than a set
// of tabs that appears/reorders with the data.
const MODE_TABS = {
  review: [
    { id: 'all', label: 'All', match: () => true },
    { id: 'await', label: 'Awaiting review', match: (t) => t.status_key === 'pending-review' },
    { id: 'approval', label: 'Needs approval', match: (t) => t.status_key === 'pending-approval' },
    { id: 'revised', label: 'Revised', match: (t) => t.status_key === 're-review' },
    { id: 'flagged', label: 'Flagged', match: (t) => t.status_key === 'breach' || t.is_anomaly === 1 || t.is_anomaly === true },
    { id: 'approved', label: 'Approved', match: (t) => t.status_key === 'approved' },
  ],
  manager: [
    { id: 'active', label: 'Active', match: (t) => t.status_key !== 'approved' && t.status_key !== 'submitted' },
    { id: 'open', label: 'Open', match: (t) => t.status_key === 'ready' },
    { id: 'inprog', label: 'In progress', match: (t) => t.status_key === 'in-progress' },
    { id: 'overdue', label: 'Overdue', match: (t) => t.overdue === 1 || t.overdue === true },
    { id: 'sentback', label: 'Sent back', match: (t) => t.status_key === 'returned' },
    { id: 'completed', label: 'Completed', match: (t) => t.status_key === 'approved' || t.status_key === 'submitted' },
  ],
};

// The actionable state comes from the doc's LIFECYCLE status name (client-
// configured); `task_status` is only Open/Completed/Cancelled/Unassigned.
// Same heuristics as taskList.js — order matters (returned outranks in-progress).
const RX_RETURNED = /return|sent back|revis|reject|bounce/i;
const RX_INPROGRESS = /progress|draft|started|working/i;

function managerStatusKey(t) {
  const task = String((t && t.task_status) || '');
  if (/complete/i.test(task)) return 'approved';
  const s = String((t && (t.lifecycle_status_name || t.doc_status_text)) || '');
  if (RX_RETURNED.test(s)) return 'returned';
  if (RX_INPROGRESS.test(s)) return 'in-progress';
  return 'ready';
}
function managerOverdue(t) {
  if (t.overdue === 1 || t.overdue === true) return true;
  if (!t.due_date) return false;
  const d = new Date(t.due_date);
  return !isNaN(d.getTime()) && d.getTime() < Date.now();
}

export function taskTable(options) {
  const {
    http,
    getTasks = () => [],
    getTasksLoading = () => false,
    getLimit = () => 8,
    getScope = () => 'all',
    getMode = () => 'review',
    getDocTypes = () => [],
    onReady = () => {},
  } = options || {};

  const limit = () => getLimit() || 8;
  const isManager = () => getMode() === 'manager';

  const self = {
    rows: ref([]),
    loading: ref(false),
    q: ref(''),
    searchOpen: ref(false),
    activeTab: ref(isManager() ? 'active' : 'all'),
    showAll: ref(false),
  };

  self.mode = computed(() => (isManager() ? 'manager' : 'review'));
  self.isLoading = computed(() => (isManager() ? !!getTasksLoading() : self.loading.value));

  // The full queue (drives the tab counts). Manager rows are the user's own
  // tasks normalised into the canonical { status_key, overdue } shape. The
  // doc-type include filter applies in BOTH modes (rows carry `doc_type` —
  // manager from the task list, review from the queue's template join).

  self.scoped = computed(() => {
    const base = isManager()
      ? (getTasks() || [])
          .filter((t) => getScope() !== 'mine' || t.mine === '1' || t.mine === 1 || t.mine === true)
          .map((t) => ({ ...t, status_key: managerStatusKey(t), overdue: managerOverdue(t) }))
      : self.rows.value;
    const docTypes = getDocTypes() || [];
    if (!docTypes.length) return base;
    const want = docTypes.map((d) => String(d).toLowerCase());
    return base.filter((r) => want.includes(String(r.doc_type || '').toLowerCase()));
  });
  self.taskCount = computed(() => self.scoped.value.length);

  // Fixed persona tabs with live counts (prototype parity). `tabs` includes the
  // "All"/"Active" tab; `activeTab` is a tab id.
  self.tabDefs = computed(() => MODE_TABS[self.mode.value] || MODE_TABS.review);
  self.tabs = computed(() =>
    self.tabDefs.value.map((t) => ({ id: t.id, label: t.label, count: self.scoped.value.filter(t.match).length })),
  );
  self.activeTabDef = computed(
    () => self.tabDefs.value.find((t) => t.id === self.activeTab.value) || self.tabDefs.value[0],
  );

  self.filtered = computed(() => {
    const def = self.activeTabDef.value;
    let list = def ? self.scoped.value.filter(def.match) : self.scoped.value;
    const ql = self.q.value.trim().toLowerCase();
    if (ql) {
      // Manager searches what the row shows (title + reference, prototype
      // parity); review keeps the broader scope (owner + levels).
      list = list.filter((r) => {
        const hay = isManager()
          ? `${r.doc_name || ''} ${r.doc_title || ''} ${r.reference || ''}`
          : `${r.doc_name || ''} ${r.doc_title || ''} ${r.reference || ''} ${r.owner_name || ''} ${r.selected_level || ''} ${r.suggested_level || ''}`;
        return hay.toLowerCase().includes(ql);
      });
    }
    return list;
  });

  self.shownRows = computed(() => (self.showAll.value ? self.filtered.value : self.filtered.value.slice(0, limit())));
  self.overdueCount = computed(() => self.filtered.value.filter((r) => self.isOverdue(r)).length);

  // --- mode-aware chrome ---
  self.searchPlaceholder = computed(() => (isManager() ? 'Search tasks…' : 'Search submissions…'));
  self.footerNoun = computed(() => (isManager() ? 'task' : 'submission'));
  self.emptyText = computed(() => {
    const ql = self.q.value.trim();
    return `Nothing in this view${ql ? ` matches “${ql}”` : ''}.`;
  });
  self.closeSearch = () => {
    self.q.value = '';
    self.searchOpen.value = false;
  };

  // --- per-row presentation ---
  self.isOverdue = (r) => r.overdue === true || r.overdue === 1;
  self.meta = (r) => {
    const metaMap = isManager() ? MANAGER_STATUS_META : STATUS_META;
    return metaMap[r && r.status_key] || FALLBACK_META;
  };
  self.statusLabel = (r) => self.meta(r).label || (r && r.lifecycle_status_name) || (r && r.status_key) || '';
  self.statusColour = (r) => self.meta(r).colour;
  self.statusIcon = (r) => self.meta(r).icon;
  self.actionLabel = (r) => self.meta(r).action;
  self.actionKind = (r) => 'v3-tt-btn--' + self.actionLabel(r).toLowerCase();

  self.ownerName = (r) => (r && r.owner_name ? String(r.owner_name).trim() : '');
  self.initials = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    if (!parts[0]) return '–';
    return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };
  self.isAnomaly = (r) => !!r && (r.is_anomaly === true || r.is_anomaly === 1 || r.status_key === 'breach');
  self.selectedLevel = (r) => (r && r.selected_level ? String(r.selected_level) : '');
  self.suggestedLevel = (r) => (r && r.suggested_level ? String(r.suggested_level) : '');
  // The level outcome + the anomaly criterion when they disagree:
  //   anomaly → "Recommended 9, levelled at 3" (system suggestion vs final level)
  //   normal  → "Levelled at 3"
  self.levelText = (r) => {
    const sel = self.selectedLevel(r);
    const sug = self.suggestedLevel(r);
    if (self.isAnomaly(r) && sug && sel) return `Recommended ${sug}, levelled at ${sel}`;
    if (sel) return `Levelled at ${sel}`;
    if (sug) return `Recommended ${sug}`;
    return '';
  };
  // Manager rows carry the task type as the document subtitle (prototype: the
  // homepage must never assume "evaluation"); review rows show the level line.
  self.docSub = (r) => (isManager() ? String(r.task_type || '') : self.levelText(r));

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  self.activityWhen = (r) => {
    if (r.due_date) return 'Due ' + fmtDate(r.due_date);
    if (!isManager() && r.submitted_at) return 'Submitted ' + fmtDate(r.submitted_at);
    if (isManager()) return '—';
    return r.lifecycle_status_name || '—';
  };
  self.activitySub = (r) => {
    if (self.isOverdue(r)) return 'Overdue';
    if (isManager()) return '';
    return r.lifecycle_status_name || '';
  };

  // --- data ---
  async function load() {
    self.loading.value = true;
    try {
      const resp = await http.post('Report/reviewQueue', { scope: getScope() || 'all', limit: SERVER_LIMIT });
      const data = resp && resp.data;
      self.rows.value = Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  const ready = () => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  };
  if (isManager()) {
    // Manager rows come from the injected task getters — signal ready once the
    // store finishes its (possibly already-complete) load. The widget owns init.
    watch(
      () => !!getTasksLoading(),
      (loading) => {
        if (!loading) ready();
      },
      { immediate: true },
    );
  } else {
    watch(
      () => getScope(),
      async () => {
        await load();
        ready();
      },
      { immediate: true },
    );
  }

  return self;
}
